import React, { useState, useEffect, useMemo } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Plus, Pencil, Trash2, Search, Tag } from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getPaginationPages, getTotalPages, paginate } from '@/utils/pagination'
import { fetchAllRows } from '@/utils/supabasePagination'
import { useToast } from '@/hooks/use-toast'
import { getQuestionImagePublicUrl } from '@/utils/imageUtils'

interface Question {
  id: string
  text: string
  choices: Array<{
    id: string
    text: string
    label?: string
  }>
  correctAnswerId: string
  category?: string
  image_path?: string
}

const AdminQuestions = () => {
  const { toast } = useToast()

  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [questionPage, setQuestionPage] = useState(1)
  const [questionsPerPage, setQuestionsPerPage] = useState(5)

  // Fetch questions from Supabase
  const fetchQuestionsWithChoices = async () => {
    try {
      return await fetchAllRows<any>(
        'questions',
        `
      *,
      choices (
        id,
        label,
        text,
        is_correct
      )
    `,
        (q) => q.order('created_at', { ascending: false })
      )
    } catch (error) {
      console.error('Error fetching questions:', error)
      return []
    }
  }

  // Transform Supabase data to match existing Question type
  const transformSupabaseData = (supabaseData: any[]): Question[] => {
    return supabaseData.map((item) => {
      // Sort choices by label (A, B, C, D, etc.)
      const sortedChoices =
        item.choices?.sort((a: any, b: any) => {
          return (a.label || a.id).localeCompare(b.label || b.id)
        }) || []

      const correctChoice = sortedChoices?.find((c: any) => c.is_correct)

      return {
        id: item.id,
        text: item.text,
        choices: sortedChoices.map((c: any) => ({
          id: c.label || c.id,
          label: c.label,
          text: c.text,
        })),
        correctAnswerId: correctChoice?.label || correctChoice?.id || '',
        category: item.category,
        image_path: item.image_path,
      }
    })
  }

  // Load questions on component mount
  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true)
      try {
        const supabaseData = await fetchQuestionsWithChoices()
        const transformedQuestions = transformSupabaseData(supabaseData)
        setQuestions(transformedQuestions)
      } catch (error) {
        console.error('Error loading questions:', error)
      } finally {
        setLoading(false)
      }
    }

    loadQuestions()
  }, [])

  // Get unique categories
  const categories = useMemo(
    () => ['all', ...Array.from(new Set(questions.map((q) => q.category || 'Diğer')))],
    [questions]
  )

  // Filter questions based on search query and category filter
  const filteredQuestions = useMemo(
    () =>
      questions.filter((question) => {
        const matchesSearch = question.text.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr'))
        const matchesCategory =
          categoryFilter === 'all' || (question.category || 'Diğer') === categoryFilter
        return matchesSearch && matchesCategory
      }),
    [questions, searchQuery, categoryFilter]
  )

  const handleDeleteQuestion = async (id: string) => {
    const { error: questionError } = await supabase.from('questions').delete().eq('id', id)
    if (questionError) {
      const isTestQuestionsError =
        questionError.message?.includes('test_questions') ||
        questionError.message?.includes('violates foreign key constraint')

      toast({
        description: isTestQuestionsError
          ? 'Bu soru bir teste atanmış olduğu için silinemiyor. Önce test ile ilişkisini kaldırmalısınız.'
          : 'Soru silinirken bir hata oluştu!',
        variant: 'destructive',
      })
      return
    }

    setQuestions((prev) => prev.filter((q) => q.id !== id))

    toast({
      description: 'Soru ve seçenekleri başarıyla silindi!',
    })
  }

  //Pagination
  const questionTotalPages = getTotalPages(filteredQuestions.length, questionsPerPage)
  const paginatedQuestions = paginate(filteredQuestions, questionPage, questionsPerPage)

  useEffect(() => {
    setQuestionPage(1)
  }, [searchQuery, categoryFilter, questionsPerPage])

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    )
  }
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold">Soru Yönetimi</h1>
          <Link to="/admin/questions/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Soru Ekle
            </Button>
          </Link>
        </div>

        <div className="flex w-full items-center gap-2 mb-6">
          <div className="flex-1 flex items-center gap-2 min-w-[180px]">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Soruları ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex-1 flex items-center justify-center gap-2 min-w-[220px]">
            <Tag className="h-5 w-5 text-gray-400" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full max-w-[260px]">
                <SelectValue placeholder="Kategori Filtresi" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category === 'all' ? 'Tüm Kategoriler' : category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 flex items-center justify-end gap-2 min-w-[110px]">
            <span className="text-sm text-gray-500">Sayfa başı:</span>
            <Select
              value={questionsPerPage.toString()}
              onValueChange={(value) => {
                setQuestionsPerPage(Number(value))
                setQuestionPage(1)
              }}
            >
              <SelectTrigger className="w-16">
                <SelectValue placeholder="Sayfa başı" />
              </SelectTrigger>
              <SelectContent>
                {[5, 10, 20, 50].map((opt) => (
                  <SelectItem key={opt} value={opt.toString()}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {paginatedQuestions.length > 0 ? (
          <div className="space-y-4">
            {paginatedQuestions.map((question, idx) => (
              <Card key={question.id} className="overflow-hidden">
                <CardHeader className="bg-gray-50 py-3 px-4">
                  <div className="flex justify-between items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div
                        dangerouslySetInnerHTML={{ __html: question.text }}
                        className="prose prose-sm max-w-none prose-ol:list-decimal prose-ul:list-disc prose-p:my-0 mb-2"
                      />
                    </div>

                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap">
                        <Tag className="h-3 w-3 mr-1" />
                        {question.category || 'Diğer'}
                      </span>
                      <Link to={`/admin/questions/edit/${question.id}`}>
                        <Button variant="ghost" size="sm">
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Soruyu Silmek İstediğinize Emin Misiniz?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Bu işlem geri alınamaz. Bu soruyu sildiğinizde, tüm veriler kalıcı
                              olarak kaldırılacaktır.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>İptal</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteQuestion(question.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Sil
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Add image section before choices */}
                  {question.image_path && (
                    <div className="mb-4">
                      <img
                        src={getQuestionImagePublicUrl(question.image_path)}
                        alt="Soru görseli"
                        className="w-full max-h-64 object-contain rounded-md shadow-sm"
                        onError={(e) => {
                          console.error('Image load failed:', question.image_path)
                          e.currentTarget.src = '/placeholder-image.png'
                        }}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {question.choices.map((choice) => (
                      <div
                        key={choice.id}
                        className={`flex items-center gap-2 border rounded-md p-2 ${
                          choice.id === question.correctAnswerId
                            ? 'bg-green-50 border-green-300'
                            : ''
                        }`}
                      >
                        <div
                          className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium ${
                            choice.id === question.correctAnswerId
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100'
                          }`}
                        >
                          {choice.id.toUpperCase()}
                        </div>
                        <span>{choice.text}</span>
                        {choice.id === question.correctAnswerId && (
                          <span className="ml-auto text-xs font-medium text-green-600">
                            Doğru Cevap
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {searchQuery || categoryFilter !== 'all'
                ? 'Arama kriterine uygun soru bulunamadı.'
                : 'Henüz soru bulunamadı.'}
            </p>
            {(searchQuery || categoryFilter !== 'all') && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('')
                  setCategoryFilter('all')
                }}
              >
                Filtreleri Temizle
              </Button>
            )}
          </div>
        )}

        {/* Pagination UI */}
        {questionTotalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuestionPage((p) => Math.max(p - 1, 1))}
              disabled={questionPage === 1}
            >
              Önceki
            </Button>
            {getPaginationPages(questionPage, questionTotalPages).map((page, idx) =>
              page === '...' ? (
                <span key={'ellipsis-' + idx} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={page + '-' + idx}
                  variant={questionPage === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuestionPage(page)}
                >
                  {page}
                </Button>
              )
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuestionPage((p) => Math.min(p + 1, questionTotalPages))}
              disabled={questionPage === questionTotalPages}
            >
              Sonraki
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminQuestions
